import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, ExternalLink, ChevronRight, Users, FolderOpen, FileText, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchAkademikFaaliyet } from '../../utils/akademikFaaliyetCache';

const GRADIENT_COLORS = [
  'from-blue-500 to-purple-600',
  'from-green-500 to-teal-600',
  'from-orange-500 to-red-600',
  'from-purple-500 to-pink-600',
  'from-teal-500 to-cyan-600',
  'from-red-500 to-orange-600',
];

const TUR_COLORS = {
  'SCI/SSCI': 'bg-blue-100 text-blue-700',
  'Ulusal': 'bg-green-100 text-green-700',
  'ESCI': 'bg-orange-100 text-orange-700',
  'AHCI': 'bg-yellow-100 text-yellow-700',
  'Uluslararasi Diger': 'bg-purple-100 text-purple-700',
};

const Q_COLORS = {
  'q1': 'bg-green-600 text-white',
  'q2': 'bg-blue-500 text-white',
  'q3': 'bg-yellow-500 text-white',
  'q4': 'bg-gray-400 text-white',
};

const PublicationsSection = () => {
  const { t } = useTranslation();
  const [bolumler, setBolumler] = useState([]);
  const [makaleler, setMakaleler] = useState([]);
  const [projeler, setProjeler] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAkademikFaaliyet()
      .then(data => {
        setBolumler(data.bolumler);
        setMakaleler(data.makaleler);
        setProjeler(data.projeler);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // En son 6 SCI/SSCI makale
  const recentPublications = useMemo(() => {
    return [...makaleler]
      .filter(m => m.makale_adi)
      .sort((a, b) => (b.donem || 0) - (a.donem || 0))
      .slice(0, 6);
  }, [makaleler]);

  const formatDOI = doi => {
    if (!doi) return null;
    return doi.startsWith('http') ? doi : `https://doi.org/${doi}`;
  };

  const stats = [
    { label: 'Akademik Yayın', value: makaleler.length, icon: BookOpen, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Araştırma Projesi', value: projeler.length, icon: FolderOpen, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Bölüm', value: bolumler.length, icon: Users, color: 'text-purple-600', bg: 'bg-purple-50' },
  ];

  return (
    <div className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
      {/* Dekoratif arka plan */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-teal-400/10 to-cyan-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 relative z-10">

        {/* Başlık */}
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center justify-center p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mb-6">
            <div className="flex items-center space-x-2 bg-white rounded-full px-6 py-3">
              <BookOpen className="w-5 h-5 text-blue-600" />
              <span className="text-blue-600 font-semibold text-sm">Hacettepe Akademik Faaliyet Sistemi</span>
            </div>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5 leading-tight">
            Bilimsel
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Araştırmalarımız</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Fen Fakültesi bölümlerinin güncel akademik yayın ve proje verileri otomatik olarak listelenmektedir.
          </p>
        </motion.div>

        {/* İstatistik Kartları */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-md p-6 animate-pulse flex items-center gap-4">
                <div className="w-14 h-14 bg-gray-200 rounded-xl" />
                <div className="flex-1">
                  <div className="h-8 bg-gray-200 rounded w-24 mb-2" />
                  <div className="h-4 bg-gray-100 rounded w-32" />
                </div>
              </div>
            ))
          ) : stats.map((s, i) => (
            <motion.div
              key={s.label}
              className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 p-6 flex items-center gap-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <div className={`p-4 rounded-xl ${s.bg} flex-shrink-0`}>
                <s.icon size={28} className={s.color} />
              </div>
              <div>
                <p className="text-3xl font-extrabold text-gray-900">
                  {s.value > 0 ? s.value.toLocaleString('tr-TR') : '—'}
                </p>
                <p className="text-sm text-gray-500 mt-0.5">{s.label}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Son Yayınlar */}
        {loading ? (
          <div className="flex flex-col items-center gap-3 py-10">
            <Loader className="animate-spin text-blue-500" size={32} />
            <p className="text-gray-400 text-sm">Yayınlar yükleniyor...</p>
          </div>
        ) : recentPublications.length > 0 ? (
          <>
            <motion.h3
              className="text-xl font-bold text-gray-700 mb-6 flex items-center gap-2"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <FileText size={20} className="text-blue-500" />
              Son Yayınlar
            </motion.h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentPublications.map((pub, index) => (
                <motion.div
                  key={index}
                  className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 flex flex-col"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  whileHover={{ y: -5 }}
                >
                  {/* Renk şeridi */}
                  <div className={`h-1.5 bg-gradient-to-r ${GRADIENT_COLORS[index % GRADIENT_COLORS.length]}`} />

                  <div className="p-5 flex flex-col flex-1">
                    {/* Etiketler */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {pub.makale_turu && (
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${TUR_COLORS[pub.makale_turu] || 'bg-gray-100 text-gray-600'}`}>
                          {pub.makale_turu}
                        </span>
                      )}
                      {pub.q_dilimi && (
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${Q_COLORS[(pub.q_dilimi || '').toLowerCase()] || 'bg-gray-200 text-gray-600'}`}>
                          {pub.q_dilimi.toUpperCase()}
                        </span>
                      )}
                      {pub.bolum && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">
                          {pub.bolum}
                        </span>
                      )}
                    </div>

                    {/* Başlık */}
                    <h3 className="text-sm font-bold text-gray-800 mb-2 leading-snug group-hover:text-blue-600 transition-colors flex-1 line-clamp-3">
                      {pub.makale_adi}
                    </h3>

                    {/* Yazarlar */}
                    <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                      <span className="font-medium text-gray-700">{pub.sorumlu_yazar}</span>
                      {pub.yazarlar && pub.yazarlar !== pub.sorumlu_yazar && (
                        <span className="text-gray-400"> — {pub.yazarlar}</span>
                      )}
                    </p>

                    {/* Dergi */}
                    <p className="text-xs italic text-gray-400 mb-4 truncate">{pub.dergi_adi}</p>

                    {/* Alt */}
                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-50">
                      <span className="text-xs font-medium text-gray-400">{pub.donem}</span>
                      {pub.doi && (
                        <a
                          href={formatDOI(pub.doi)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline font-semibold"
                          onClick={e => e.stopPropagation()}
                        >
                          <ExternalLink size={12} /> DOI
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        ) : null}

        {/* CTA Butonu */}
        <motion.div
          className="text-center mt-14"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Link
            to="/akademik-faaliyet"
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl"
          >
            <span>Tüm Akademik Faaliyetleri Gör</span>
            <ChevronRight className="w-5 h-5" />
          </Link>
          <p className="text-gray-500 mt-4 text-sm">
            Makaleler, projeler ve daha fazlası bölüm bazında filtrelenebilir
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PublicationsSection;
